"use client";

import { styled } from "@mui/material/styles";
import { LayoutContent } from "components/Layout";
import ReturnLink from "components/ReturnLink";
import { CurationBreadcrumbs, useBreadcrumbItems } from "containers/Curation/CurationBreadcrumbs";
import { CurationSiblingProps, CurationSiblings } from "containers/Curation/CurationSiblings";
import {
    addToCart,
    useProductCheckAvailabilityEvent,
    useViewItem,
} from "containers/GoogleAnalytics/googleAnalyticsEvents";
import { useBreadcrumbListQuery, useCurationContentQuery } from "gql";
import { useConsumerTripInfo } from "hooks/useConsumerTripInfo";
import { useEntityConfigurationValueByKey } from "hooks/useEntityConfiguration";
import { useFavouriteButton } from "hooks/useFavouriteButton";
import { useHeaderConfiguration } from "hooks/useHeaderEntityConfiguration";
import { useIsCurationBreadcrumbsEnabled } from "hooks/useIsCurationBreadcrumbsEnabled";
import { useBookingExternalReference } from "hooks/usePersistedQueryParams";
import { getNewIdSelector } from "utils/consumerTrip/model/types";

import { FadeUpAnimationWrapper } from "@holibob-packages/ui-core/components";
import { ProductFullLoadedV2 } from "@holibob-packages/ui-core/containers";
import { ProductQuery } from "@holibob-packages/ui-core/gql-request";
import { useRouter } from "@holibob-packages/ui-core/navigation";

export type ProductPageV2Props = {
    product: ProductQuery["product"];
};
export const ProductPageV2 = ({ product }: ProductPageV2Props) => {
    const { push } = useRouter();
    const withSectionFadeUpAnimation =
        useEntityConfigurationValueByKey<boolean>("SETTING.SECTION_FADE_UP_ANIMATION") ?? false;
    const showOperatedBy = useEntityConfigurationValueByKey("SETTING.SHOW_OPERATED_BY") ?? true;
    const showSourcedFrom = useEntityConfigurationValueByKey("SETTING.SHOW_SOURCED_FROM") ?? false;
    const showSiblingCurations = useEntityConfigurationValueByKey("SETTING.ENABLED_CURATION_SIBLINGS") ?? false;
    const showBreadcrumbs = useIsCurationBreadcrumbsEnabled();
    const checkAvailabilityEvent = useProductCheckAvailabilityEvent();

    const {
        isAvailable: showFavouriteButton,
        isFavourite,
        setIsFavourite,
    } = useFavouriteButton(product.id, product.isFavourite ?? false);

    useViewItem(product.id, product.name, product.guidePrice, product.guidePriceCurrency);
    const appHeaderConfig = useHeaderConfiguration();
    const { consumerTripId, consumerId } = useConsumerTripInfo();
    const consumerTripBookingInput = {
        selector: getNewIdSelector(consumerTripId),
        consumer: { selector: getNewIdSelector(consumerId) },
    };

    const onCheckAvailabilityHandler = () => {
        checkAvailabilityEvent(product.id);
    };

    return (
        <>
            <ProductFullLoadedV2
                product={product}
                bookingReference={useBookingExternalReference()}
                onBookingCompleted={({ id: bookingId }) => {
                    addToCart(product.id, product.name, product.guidePrice, product.guidePriceCurrency);
                    void push(`/booking/${bookingId}`);
                }}
                withStickyAppBar={appHeaderConfig.isSticky}
                withSectionFadeUpAnimation={withSectionFadeUpAnimation}
                breadcrumbs={showBreadcrumbs && <CurationBreadcrumbs />}
                showFavouriteButton={showFavouriteButton}
                isFavourite={isFavourite}
                onFavouriteChange={setIsFavourite}
                showOperatedBy={!!showOperatedBy}
                showSourcedFrom={!!showSourcedFrom}
                consumerTrip={consumerTripBookingInput}
                onCheckAvailability={onCheckAvailabilityHandler}
            >
                {showSiblingCurations && (
                    <ProductCurationSiblings withSectionFadeUpAnimation={withSectionFadeUpAnimation} />
                )}
            </ProductFullLoadedV2>
        </>
    );
};

const SiblingCurationsWrapperContainer = styled(LayoutContent)(({ theme }) => ({
    [theme.breakpoints.up("lg")]: {
        padding: 0,
    },
}));

function ProductCurationSiblings(props: { withSectionFadeUpAnimation?: boolean }) {
    const { withSectionFadeUpAnimation } = props;
    const { items } = useBreadcrumbItems();
    const canDisplaySiblingCurations = items.length > 1;
    const { data: breadcrumbData } = useBreadcrumbListQuery({
        variables: { items },
        skip: !canDisplaySiblingCurations,
    });
    const breadcrumbs = breadcrumbData?.breadcrumbList.nodes ?? [];
    const currentCurationId = breadcrumbs.length > 0 ? breadcrumbs[breadcrumbs.length - 1] : undefined;

    if (!currentCurationId) {
        return null;
    }

    return (
        <FadeUpAnimationWrapper isEnabled={withSectionFadeUpAnimation}>
            <SiblingCurationsWrapperContainer>
                <ProductCurationSiblingsContent curationId={currentCurationId.id} curationIdsPath={items} />
                <ReturnLink />
            </SiblingCurationsWrapperContainer>
        </FadeUpAnimationWrapper>
    );
}

type ProductCurationSiblingsProps = {
    curationId: string;
} & Omit<CurationSiblingProps, "currentCurationId" | "parentName" | "curationSiblings">;

function ProductCurationSiblingsContent({ curationId, curationIdsPath, ...props }: ProductCurationSiblingsProps) {
    const { data } = useCurationContentQuery({ variables: { curationId } });
    const curationContent = data?.curationContent;

    if (!curationContent) return null;

    const { curation, siblings, curationParent } = curationContent;

    if (!curationParent) return null;

    return (
        <CurationSiblings
            currentCurationId={curation.id}
            curationSiblings={siblings}
            parentName={curationParent.name ?? ""}
            curationIdsPath={curationIdsPath}
            {...props}
        />
    );
}
